import React, { useId, useState } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { photosIn } from '../constants/galleries';
import PhotoStack from './PhotoStack';
import Print from './Print';

/**
 * One item row. Optional photo (always outside the text column on desktop) and
 * an optional panel that opens in place.
 *
 * The whole head of the row is the control, not a separate "open" button: the
 * title is what you'd reach for anyway, and a repeated coloured chip down the
 * page turns the accent into noise. Markup is the standard accordion pattern —
 * a heading containing a button — with the button's hit area stretched over the
 * head by a positioned ::after, so the target is the whole row.
 *
 * Expansion is handled by @formkit/auto-animate: the panel is added to or
 * removed from the DOM and the library animates between the two layouts.
 */
export default function Record({
  when,
  title,
  role,
  say,
  story,
  gallery,
  cover = false,
  photoCaption,
  link,
  enlargeLabel,
  onOpenPhotos,
}) {
  const [open, setOpen] = useState(false);
  const [foldRef] = useAutoAnimate({ duration: 240, easing: 'cubic-bezier(.2,.7,.3,1)' });
  const panelId = useId();

  const photos = gallery ? photosIn(gallery) : [];
  const showCover = cover && photos.length > 0;
  const expandable = Boolean(story);

  const caption = photoCaption || (when ? `${title}, ${when}` : title);

  const head = (
    <>
      {when && <p className="when">{when}</p>}
      <h3>
        {expandable ? (
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panelId}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="label">{title}</span>
            <span className="chev" aria-hidden="true">›</span>
          </button>
        ) : (
          title
        )}
      </h3>
      {role && <p className="role">{role}</p>}
      {say && <p className="say">{say}</p>}
    </>
  );

  return (
    <div className={`rec${showCover ? ' with-photo' : ''}${open ? ' is-open' : ''}`}>
      <div className={`rec-head${expandable ? ' is-expandable' : ''}`}>{head}</div>

      {expandable && (
        <div className="fold" id={panelId} ref={foldRef}>
          {open && (
            <div className={`panel${photos.length ? ' has-photos' : ''}`}>
              <p>{story}</p>

              {link && (
                <p>
                  <a className="linkout" href={link.url} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                </p>
              )}

              {photos.length > 0 && (
                <PhotoStack
                  photos={photos}
                  caption={caption}
                  enlargeLabel={enlargeLabel}
                  onOpen={(n) => onOpenPhotos(photos, n, caption)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* The cover belongs to the row, so it opens the row — the same thing
          the title does, which is what it already looks like it is doing when
          it fades and hands over to the panel's larger copy of itself. Jumping
          straight to the light table skipped a step and left the story behind
          a separate click. From the panel the pile still enlarges, so the way
          to the light table is cover, panel, enlarge.
          A cover on a row with no story has nowhere to open to, so that one
          goes on opening the photograph. */}
      {showCover && (
        <Print
          photos={photos}
          alt={caption}
          caption={caption}
          expanded={expandable ? open : undefined}
          controls={expandable ? panelId : undefined}
          onOpen={
            expandable ? () => setOpen(true) : (n) => onOpenPhotos(photos, n, caption)
          }
        />
      )}
    </div>
  );
}
